import { type ItemDetails, LegacySegmentTypes } from '@1debit/analytics';
import { type ElementType, type MouseEventHandler } from 'react';

import * as subcomponents from './subcomponents';

import { type AnalyticsId } from '../../client/AnalyticsProvider/AnalyticsProvider';
import { useClickableElementAnalytics } from '../../client/AnalyticsProvider/hooks/useClickableElementAnalytics';
import { classNames } from '../../server/utils/classNames';

export type ListItemContentProps = subcomponents.ListItemContentProps;
export type BulletListItemContentProps = subcomponents.BulletListItemContentProps;

export type ListItemDefaultProps = {
	tag?: keyof JSX.IntrinsicElements | ElementType;
	className?: string;
	disabled?: boolean;
	children?: React.ReactNode;
	htmlFor?: string;
	variant?: 'standard' | 'compact';
};

type ClickableListItemPropsWithOnClick = ListItemDefaultProps & {
	onClick: MouseEventHandler<HTMLElement>;
	analyticsId: AnalyticsId;
};

type ClickableListItemProps = { itemValue?: string; itemDetails?: ItemDetails } & (
	| ClickableListItemPropsWithOnClick
	| ClickableListItemPropsWithHref
);

type ClickableListItemPropsWithHref = ListItemDefaultProps & {
	href: string;
	analyticsId: AnalyticsId;
};

export type ListItemProps = ListItemDefaultProps &
	(ClickableListItemProps | { onClick?: undefined; href?: undefined; analyticsId?: undefined });

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const isListItemWithOnClick = (props: any): props is ClickableListItemPropsWithOnClick => {
	return 'onClick' in props;
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const isListItemWithHref = (props: any): props is ClickableListItemPropsWithHref => {
	return 'href' in props;
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const isClickableListItem = (props: any): props is ClickableListItemProps => {
	return isListItemWithOnClick(props) || isListItemWithHref(props);
};

export const ListItem = ({
	tag: Wrapper,
	className,
	children,
	disabled,
	variant = 'standard',
	analyticsId,
	...props
}: ListItemProps) => {
	const isInteractive = !!(isClickableListItem(props) || props.htmlFor);
	let onClick = isListItemWithOnClick(props) ? props.onClick : undefined;
	let href = isListItemWithHref(props) ? props.href : undefined;
	const type = href
		? LegacySegmentTypes.ElementType.ELEMENT_TYPE_LINK
		: LegacySegmentTypes.ElementType.ELEMENT_TYPE_BUTTON;
	const itemDetails = isClickableListItem(props) ? props.itemDetails : undefined;
	const itemValue = isClickableListItem(props) ? props.itemValue : undefined;
	const { handleClick } = useClickableElementAnalytics(analyticsId ?? null, type, itemDetails, itemValue);

	const styles = classNames(
		'group flex flex-col px-6 grow text-left rounded-lg',
		'origin-center transition-transform',
		'min-w-0 w-full',
		'text-content-primary',
		{ 'py-2': variant === 'compact' },
		{ 'py-4': variant === 'standard' },
		{
			'hover:bg-bg-raised-secondary hover:rounded-xl active:scale-[.98]': !disabled && isInteractive,
		},
		{ 'opacity-40 cursor-not-allowed': disabled },
		className,
	);

	// Since non-form buttons don't have an intrinsic disabled state, we need to
	// manually disable the list item by removing the interactivity.
	if (disabled) {
		onClick = undefined;
		href = '';
		props.htmlFor = '';
	}

	if (!Wrapper) {
		return (
			<li className={styles} {...props}>
				{children}
			</li>
		);
	}

	return (
		<li className="flex grow">
			<Wrapper className={styles} disabled={disabled} {...props} href={href} onClick={handleClick(onClick)}>
				{children}
			</Wrapper>
		</li>
	);
};

ListItem.ListContent = subcomponents.ListItemContent;
ListItem.BulletListContent = subcomponents.BulletListItemContent;
